/**
 * StockLedgerScreen — one product's stock movements with a running balance that
 * reconciles to live stock. The shrinkage-investigation and audit view.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, Search, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import {
  loadStockLedger,
  loadLedgerProducts,
  type LedgerProductOption,
} from '@/reports/stockLedger/stockLedgerDataSource';
import { buildStockLedger, type StockLedger } from '@/reports/stockLedger/stockLedger';

// ─── Component ───────────────────────────────────────────────────────────────

export function StockLedgerScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [products, setProducts] = useState<LedgerProductOption[]>([]);
  const [selected, setSelected] = useState<LedgerProductOption | null>(null);
  const [search, setSearch]     = useState('');
  const [showList, setShowList] = useState(false);

  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]     = useState<StockLedger | null>(null);
  const [meta, setMeta]     = useState<{ name: string; unit: string } | null>(null);
  const [loading, setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);

  // Load the product list once.
  useEffect(() => {
    if (!activeStoreId) return;
    void loadLedgerProducts(activeStoreId).then(setProducts);
  }, [activeStoreId]);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!selected) { setData(null); return; }
    setLoading(true);
    try {
      const [from, to] = range();
      const result = await loadStockLedger(selected.id, from, to);
      if (result) {
        setData(buildStockLedger(result.input));
        setMeta({ name: result.productName, unit: result.unit });
      }
    } finally {
      setLoading(false);
    }
  }, [selected, range]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products.slice(0, 50);
    return products
      .filter((p) => p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [products, search]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data || !meta) return;
    setExporting(true);
    try {
      const csv = toCsv(data.rows, [
        { header: 'Date', value: (r) => r.date.toLocaleString('en-IN') },
        { header: 'Type', value: (r) => r.typeLabel },
        { header: 'In/Out', value: (r) => r.direction },
        { header: 'Qty', value: (r) => r.delta },
        { header: 'Balance', value: (r) => r.balanceAfter },
        { header: 'Reference', value: (r) => r.referenceType ?? '' },
        { header: 'Staff', value: (r) => r.userName ?? '' },
        { header: 'Notes', value: (r) => r.notes ?? '' },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `StockLedger_${meta.name.replace(/\s+/g, '_')}_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data || !meta) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildLedgerHtml(data, meta, from, to),
        defaultFileName: `StockLedger_${meta.name.replace(/\s+/g, '_')}_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  const reconciles = data ? Math.abs(data.closingBalance - data.currentStock) < 0.001 : true;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Product picker */}
      <div className="relative">
        <div className="flex items-center gap-2 rounded-md border border-stroke-hairline/15 bg-bg-surface px-3 py-2">
          <Search size={15} className="text-fg-tertiary" />
          <input
            value={selected && !showList ? `${selected.name}${selected.sku ? ` · ${selected.sku}` : ''}` : search}
            onChange={(e) => { setSearch(e.target.value); setShowList(true); }}
            onFocus={() => { setShowList(true); setSearch(''); }}
            placeholder="Search a product to view its ledger…"
            className="flex-1 bg-transparent text-body text-fg-primary outline-none"
          />
          {selected && (
            <span className="text-label text-fg-tertiary">Stock: {selected.currentStock} {selected.unit}</span>
          )}
        </div>
        {showList && (
          <div className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-stroke-hairline/15 bg-bg-surface shadow-lg">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-body text-fg-tertiary">No products match.</div>
            ) : (
              filtered.map((p) => (
                <button key={p.id} type="button"
                  onClick={() => { setSelected(p); setShowList(false); setSearch(''); }}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-body hover:bg-bg-subtle">
                  <span className="text-fg-primary">{p.name}{p.sku && <span className="text-fg-tertiary"> · {p.sku}</span>}</span>
                  <span className="text-label text-fg-tertiary">{p.currentStock} {p.unit}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Range + actions */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading || !selected}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {!selected ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">Pick a product to see its stock ledger.</div>
      ) : loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Opening" value={`${data.openingBalance} ${meta?.unit ?? ''}`} />
            <KPI label="Stock in" value={`+${data.totalIn}`} />
            <KPI label="Stock out" value={`−${data.totalOut}`} warn={data.totalOut > 0} />
            <KPI label="Closing" value={`${data.closingBalance} ${meta?.unit ?? ''}`} accent />
          </div>

          {!reconciles && (
            <div className="rounded-lg border border-accent/30 bg-accent/5 px-4 py-2 text-body text-fg-secondary">
              Closing balance is at the end of the selected range; live stock is now {data.currentStock} {meta?.unit} (later movements exist).
            </div>
          )}

          {/* By type */}
          {data.byType.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.byType.map((t) => (
                <span key={t.type} className="rounded-md bg-bg-subtle px-2 py-1 text-label text-fg-secondary">
                  {t.label}: {t.delta > 0 ? '+' : ''}{t.delta} ({t.count})
                </span>
              ))}
            </div>
          )}

          {/* Ledger table */}
          {data.rows.length === 0 ? (
            <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-8 text-center text-body text-fg-tertiary">
              No movements in this period. Opening and closing are both {data.openingBalance} {meta?.unit}.
            </div>
          ) : (
            <Card title="Movement ledger">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-body">
                  <thead>
                    <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                      <th className="pb-1 text-left font-normal">Date</th>
                      <th className="pb-1 text-left font-normal">Type</th>
                      <th className="pb-1 text-right font-normal">Qty</th>
                      <th className="pb-1 text-right font-normal">Balance</th>
                      <th className="pb-1 text-left font-normal">Staff</th>
                      <th className="pb-1 text-left font-normal">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-stroke-hairline/10 text-fg-tertiary">
                      <td className="py-1.5" colSpan={3}>Opening balance</td>
                      <td className="py-1.5 text-right tabular-nums">{data.openingBalance}</td>
                      <td colSpan={2} />
                    </tr>
                    {data.rows.map((r, i) => (
                      <tr key={i} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5 text-fg-secondary tabular-nums">{r.date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="py-1.5">
                          <span className="flex items-center gap-1 text-fg-primary">
                            {r.direction === 'IN' ? <ArrowUpRight size={13} className="text-success-ink" />
                              : r.direction === 'OUT' ? <ArrowDownRight size={13} className="text-warning-ink" /> : null}
                            {r.typeLabel}
                          </span>
                        </td>
                        <td className={cn('py-1.5 text-right tabular-nums', r.delta < 0 ? 'text-warning-ink' : r.delta > 0 ? 'text-success-ink' : 'text-fg-tertiary')}>
                          {r.delta > 0 ? '+' : ''}{r.delta}
                        </td>
                        <td className="py-1.5 text-right font-medium tabular-nums text-fg-primary">{r.balanceAfter}</td>
                        <td className="py-1.5 text-fg-tertiary">{r.userName ?? '—'}</td>
                        <td className="py-1.5 max-w-[220px] truncate text-fg-tertiary" title={r.notes ?? ''}>{r.notes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, accent, warn }: { label: string; value: string; accent?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', warn ? 'text-warning-ink' : accent ? 'text-accent-ink' : 'text-fg-primary')}>{value}</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildLedgerHtml(d: StockLedger, meta: { name: string; unit: string }, from: Date, toExclusive: Date): string {
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const rows = d.rows.map((r) =>
    `<tr><td>${r.date.toLocaleString('en-IN')}</td><td>${r.typeLabel}</td>
     <td style="text-align:right${r.delta < 0 ? ';color:#b45309' : ''}">${r.delta > 0 ? '+' : ''}${r.delta}</td>
     <td style="text-align:right;font-weight:600">${r.balanceAfter}</td>
     <td>${r.userName ? esc(r.userName) : '—'}</td><td>${r.notes ? esc(r.notes) : '—'}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:20px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:11px;margin-top:10px}th,td{padding:5px 7px;border-bottom:1px solid #eee}
th{text-align:left;color:#666;font-weight:500}</style></head>
<body>
<h1>Stock Ledger — ${esc(meta.name)}</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · Opening ${d.openingBalance} · In +${d.totalIn} · Out −${d.totalOut} · Closing ${d.closingBalance} ${esc(meta.unit)}</p>
<table>
<thead><tr><th>Date</th><th>Type</th><th style="text-align:right">Qty</th><th style="text-align:right">Balance</th><th>Staff</th><th>Notes</th></tr></thead>
<tbody>
<tr style="color:#888"><td colspan="3">Opening balance</td><td style="text-align:right">${d.openingBalance}</td><td colspan="2"></td></tr>
${rows}
</tbody>
</table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
